/** @jsxImportSource @emotion/react */
import React, { useCallback, useState } from 'react'
import { FieldArray, FieldArrayRenderProps } from 'react-final-form-arrays'
import { FieldValidator } from 'final-form'
import { generate as generateUniqueId } from 'randomstring'
import { Button, Typography, FormHelperText } from '@mui/material'
import AddRoundedIcon from '@mui/icons-material/AddRounded'
import { ReactSortable } from 'react-sortablejs'
import { useForm } from 'react-final-form'
import ConfirmationModal from 'components/Modals/ConfirmationModal'
import * as commonClasses from 'components/TableButtons/styles'
import { Popper } from 'components/Popper'
import { getEmptyColumn } from 'components/TableConstructor/Head'
import { SectionField, SectionFieldProps, Section } from './SectionField'
import * as classes from './styles'

const DEFAULT_COLUMNS_COUNT = 3

const getEmptyTable = () =>
  JSON.stringify({
    cols: Array.from({ length: DEFAULT_COLUMNS_COUNT }).map(() => getEmptyColumn()),
    rows: [],
    isEmpty: true,
  })

// original array is an array of [0, 1, 2, ..., N - 1, N]
// updated array is an original array where one item moved to another position
// function returns original position of this item and updated one as well
function findMovedItemPosition(originalArray: number[], updatedArray: number[]) {
  for (let i = 0; i < originalArray.length; i++) {
    // N item moved to any position < (N - 1)
    if (updatedArray[i] > originalArray[i] + 1) {
      return {
        originalIndex: originalArray.findIndex((item) => item === updatedArray[i]),
        newIndex: i,
      }
    }
    // N item moved to any position > (N + 1)
    if (updatedArray[i] < originalArray[i] - 1) {
      return {
        originalIndex: originalArray.findIndex((item) => item === updatedArray[i]),
        newIndex: i,
      }
    }
  }

  for (let i = 0; i < originalArray.length; i++) {
    // N item moved to (N + 1) or (N - 1) position
    if (updatedArray[i] !== originalArray[i]) {
      return {
        originalIndex: originalArray.findIndex((item) => item === updatedArray[i]),
        newIndex: i,
      }
    }
  }

  // nothing moved
  return null
}

export type SectionFieldArrayProps = {
  name: string
  validate?: FieldValidator<Section[]>
  minCount?: number
  maxCount?: number
  itemName: string
  label?: string
  noItemsText?: string
  className?: string
  // validateField?: SectionFieldProps['validate']
  classNameField?: SectionFieldProps['className']
  removeConfirmation?: boolean
}

export type SectionFieldArrayContentProps = Omit<SectionFieldArrayProps, 'validate'> & {
  fields: FieldArrayRenderProps<Section, HTMLDivElement>['fields']
  meta: FieldArrayRenderProps<Section, HTMLElement>['meta']
}

export function SectionFieldArrayContent({
  name,
  fields,
  meta,
  minCount,
  maxCount,
  itemName,
  label,
  noItemsText,
  // validateField,
  classNameField,
  removeConfirmation,
  className,
}: SectionFieldArrayContentProps) {
  const [indexToRemove, setIndexToRemove] = useState<number | null>(null)
  const form = useForm()
  const value = (form.getFieldState(name)?.value ?? []) as Section[]

  const error = typeof meta.error === 'string' ? meta.error : undefined

  const handleAdd = useCallback((isWithTable: boolean) => {
    const newItem = {
      id: generateUniqueId(),
      title: '',
      description: '',
      table: isWithTable ? getEmptyTable() : undefined,
    }
    fields.push(newItem)
  }, [])

  const handleRemove = useCallback(
    (value: Section, index: number) => {
      if (removeConfirmation && value) {
        setIndexToRemove(index)
      } else {
        fields.remove(index)
      }
    },
    [removeConfirmation, fields.remove]
  )

  const handleConfirm = useCallback(() => {
    if (indexToRemove !== null) {
      fields.remove(indexToRemove)
      setIndexToRemove(null)
    }
  }, [indexToRemove])

  const handleSort = useCallback(
    (sortedFields: Array<{ index: number }>) => {
      const movedItemPosition = findMovedItemPosition(
        Array.from({ length: sortedFields.length }).map((_, index) => index),
        sortedFields.map(({ index }) => index)
      )

      if (movedItemPosition) {
        fields.move(movedItemPosition.originalIndex, movedItemPosition.newIndex)
      }
    },
    [fields, name]
  )

  const renderFields = () =>
    value.map((section, index) => (
      <div key={section.id}>
        <SectionField
          name={`${name}[${index}]`}
          index={index}
          value={section}
          onChange={(value) => form.change(`${name}[${index}]`, value)}
          removeDisabled={minCount !== undefined && (fields.length ?? 0) <= minCount}
          onRemove={handleRemove}
          // validate={validateField}
          className={classNameField}
        />
      </div>
    ))

  const renderPopperContent = (onClose: () => void) => (
    <ul css={classes.popperList}>
      <li>
        <Button
          css={classes.popperListButton}
          variant="outlined"
          onClick={() => {
            handleAdd(true)
            onClose()
          }}
        >
          Add Table Section
        </Button>
      </li>
      <li>
        <Button
          css={classes.popperListButton}
          variant="outlined"
          onClick={() => {
            handleAdd(false)
            onClose()
          }}
        >
          Add Details Section
        </Button>
      </li>
    </ul>
  )

  return (
    <div className={className}>
      {!!label && <Typography css={classes.label}>{label}</Typography>}
      <ReactSortable
        list={value.map((field, index) => ({ ...field, index }))}
        setList={handleSort}
        css={classes.wrapper}
      >
        {renderFields()}
      </ReactSortable>
      {!fields.length && noItemsText && <div css={classes.empty}>{noItemsText}</div>}
      <Popper trigger="onClick" getContent={renderPopperContent}>
        <Button
          css={commonClasses.addButton}
          fullWidth
          color="blue"
          size="large"
          disabled={maxCount !== undefined && (fields.length ?? 0) >= maxCount}
        >
          Add {itemName}
          <AddRoundedIcon css={commonClasses.iconPlus} />
        </Button>
      </Popper>
      {Boolean(meta.touched && error) && <FormHelperText error>{error}</FormHelperText>}
      <ConfirmationModal
        open={indexToRemove !== null}
        title={`Deleting ${itemName} #${(indexToRemove ?? 0) + 1}`}
        subtitle={`Are you sure you want to delete this ${itemName.toLowerCase()}?`}
        onClose={() => setIndexToRemove(null)}
        onConfirm={handleConfirm}
        confirmText="Delete"
        closeText="Cancel"
      />
    </div>
  )
}

export function SectionFieldArray({ name, validate, ...props }: SectionFieldArrayProps) {
  return (
    <FieldArray<Section> name={name} validate={validate}>
      {({ fields, meta }) => <SectionFieldArrayContent name={name} fields={fields} meta={meta} {...props} />}
    </FieldArray>
  )
}
